<template>
  <div v-if="stage === 'build'" class="mc-cardwrap">
    <div class="mc-file mc-build" style="--fc: var(--indigo)">
      <div class="mc-fico"><span class="mc-spin" aria-hidden="true"></span></div>
      <div class="mc-fmeta">
        <div class="mc-ft">{{ buildTitle }}</div>
        <div class="mc-fs mc-step" role="status">{{ t("maya_art_working_step") }}</div>
      </div>
      <div class="mc-buildbar"><i></i></div>
    </div>
  </div>

  <div v-else-if="stage === 'plan'" class="mc-cardwrap">
    <div class="mc-card" :class="{ 'mc-zh': zh }" style="--fc: var(--indigo)">
      <div class="mc-spine"></div>
      <div class="mc-attr">
        <img class="mc-attrav" :src="mayaImage" alt="">
        <span class="mc-who">Maya<span>{{ t("maya_art_cmo") }}</span></span>
        <span class="mc-t">{{ t("maya_art_plan_when") }}</span>
      </div>
      <div class="mc-pad">
        <div class="mc-eye"><span class="mc-dt"></span>{{ t("art_plan_title") }}<span v-if="brand" class="mc-obj"> · {{ brand }}</span></div>
        <label class="sr-only" for="maya-article-plan">{{ t("art_plan_title") }}</label>
        <textarea id="maya-article-plan" class="art-plan" v-model="planText" spellcheck="false" rows="12"></textarea>
        <div class="mc-acts">
          <button type="button" class="mc-cta art-gen" :disabled="busy" @click="$emit('generate')">{{ busy ? t("art_working") : t("art_gen_full") }}</button>
          <button type="button" class="mc-ghost art-regen" :disabled="busy" @click="$emit('regen')">{{ t("art_regen") }}</button>
        </div>
      </div>
      <div class="mc-stamp"><span class="mc-s">S</span>{{ t("maya_art_plan_stamp") }}</div>
    </div>
  </div>

  <div v-else-if="stage === 'article' && draft" class="mc-cardwrap">
    <div class="mc-card" :class="{ 'mc-zh': zh }" style="--fc: var(--indigo)">
      <div class="mc-spine"></div>
      <div class="mc-attr">
        <img class="mc-attrav" :src="mayaImage" alt="">
        <span class="mc-who">Maya<span>{{ t("maya_art_cmo") }}</span></span>
        <span class="mc-t">{{ t("maya_art_ready_when") }}</span>
      </div>
      <div class="mc-pad">
        <h2 class="mc-eye" id="maya-article-title"><span class="mc-dt"></span>{{ draft.title || t("art_plan_title") }}<span v-if="brand" class="mc-obj"> · {{ brand }}</span></h2>
        <div class="art-body" v-html="bodyHtml"></div>
        <div class="mc-acts">
          <button type="button" class="mc-ghost art-wb" @click="$emit('workbench')">{{ t("wb_open_here") }}</button>
          <a v-if="draft.url" class="mc-ghost" :href="draft.url" target="_blank" rel="noopener">{{ t("art_open_page") }}</a>
          <button type="button" class="mc-ghost art-copy" @click="$emit('copy')">{{ copied ? t("ob_copied") : t("ob_copy") }}</button>
          <a v-if="draft.published && (draft.published_url || pubLine)" class="mc-cta" :href="draft.published_url || pubLine" target="_blank" rel="noopener">{{ t("art_view_live") }}</a>
          <button v-else type="button" class="mc-cta art-publish" :disabled="busy" @click="$emit('publish', false)">{{ busy ? t("art_publishing") : (draft.published ? t("art_republish") : t("art_publish")) }}</button>
          <!-- FE-4 · out of the conversation and into the list of everything on the site.
               FE-5 · that list moved; `PAGES_HOME` is the single place that says where it is. -->
          <router-link class="mc-ghost art-allpages" :to="PAGES_HOME">{{ t("pages_see_all") }}</router-link>
        </div>
        <div v-if="pubLine || unconf" class="art-publine">
          <template v-if="unconf">
            <div class="art-unconf" role="alert">
              <div class="au-h">⚠ {{ t("art_unconf_h", { n: unconf.needs_confirm || 0 }) }}</div>
              <div v-if="unconf.notes?.length" class="au-s">{{ t("art_unconf_s") }}</div>
              <ul v-if="unconf.notes?.length" class="au-l">
                <li v-for="(note, index) in unconf.notes" :key="index">{{ note }}</li>
              </ul>
              <div class="au-a">
                <button type="button" class="mc-cta au-ask" @click="$emit('askFill')">{{ t("art_unconf_ask") }}</button>
                <button type="button" class="mc-ghost au-force" @click="$emit('publish', true)">{{ t("art_unconf_force") }}</button>
              </div>
            </div>
          </template>
          <template v-else>
            <span class="art-pubbadge">✓ {{ draft.published ? t("art_republished") : t("art_published") }}</span>
            <a v-if="pubLine" :href="pubLine" target="_blank" rel="noopener">{{ pubLine }}</a>
          </template>
        </div>
      </div>
      <div class="mc-stamp"><span class="mc-s">S</span>{{ t("maya_art_stamp") }}</div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

import { PAGES_HOME } from "../api/pages.js";  // FE-5
import { session } from "../stores/session";
import { useI18n } from "../useI18n";

defineProps({
  stage: { type: String, required: true },
  buildTitle: { type: String, default: "" },
  draft: { type: Object, default: null },
  brand: { type: String, default: "" },
  busy: { type: Boolean, default: false },
  unconf: { type: Object, default: null },
  pubLine: { type: String, default: "" },
  copied: { type: Boolean, default: false },
  bodyHtml: { type: String, default: "" },
  mayaImage: { type: String, required: true },
});
defineEmits(["generate", "regen", "publish", "copy", "workbench", "askFill"]);

const { t } = useI18n();
const zh = computed(() => session.language === "zh");
const planText = defineModel("planText", { type: String, default: "" });
</script>

<style scoped>
h2.mc-eye { margin: 0; font-size: inherit; font-weight: inherit; }
.art-plan:focus-visible, .mc-cta:focus-visible, .mc-ghost:focus-visible { outline: 3px solid var(--indigo); outline-offset: 2px; }
</style>
